import {
	BackSide,
	Box3,
	BoxGeometry,
	BufferAttribute,
	BufferGeometry,
	DoubleSide,
	Float32BufferAttribute,
	LineBasicMaterial,
	LineSegments,
	Material,
	Mesh,
	MeshBasicMaterial,
	MeshStandardMaterial,
	Vector2,
	Vector3,
} from 'three';
import { RoomVertex } from './RoomVertex';
import { ProjectRenderer } from '../ProjectRenderer';
import { CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import { RoomManager } from '../managers/RoomManager';
import { clamp, debounce } from 'lodash';
import { RenderUtils } from '../utils/RenderUtils';
import { UserDataObjectType, UserDataProp } from '../types';
import { DraggableObject } from './DraggableObject';
import RENDERER_CONFIG from '@/configs/rendererConfig';

const { ROOM_WALL } = RENDERER_CONFIG;

export class RoomWall extends DraggableObject {
	static readonly POS_Y = ROOM_WALL.POS_Y;
	static readonly THICKNESS = ROOM_WALL.THICKNESS;
	static readonly LABEL = ROOM_WALL.LABEL;
	static readonly COLORS = ROOM_WALL.COLORS;
	static readonly LAYER = ROOM_WALL.LAYER;
	static readonly MIN_LENGTH = ROOM_WALL.MIN_LENGTH;
	static readonly MAX_LENGTH = ROOM_WALL.MAX_LENGTH;
	static readonly LINES_CONGIF = ROOM_WALL.LINES;

	input!: HTMLInputElement;
	css2dObject!: CSS2DObject;
	position: Vector2;
	start: RoomVertex;
	end: RoomVertex;
	length = 0;
	private mesh: Mesh;
	private geometry: BoxGeometry;
	private edgeLines: LineSegments | null = null;
	private popover!: HTMLDivElement;
	private angle: number;
	private materials = {
		base: {
			top: new MeshStandardMaterial({
				color: RoomWall.COLORS.TOP,
				side: DoubleSide,
			}),
			bottom: new MeshStandardMaterial({
				color: 0xffffff,
				side: BackSide,
			}),
			external: new MeshStandardMaterial({
				color: RoomWall.COLORS.SIDE,
				transparent: true,
				opacity: 0.7,
			}),
			internal: new MeshStandardMaterial({
				color: RoomWall.COLORS.SIDE,
				polygonOffset: true,
				polygonOffsetFactor: 1,
				polygonOffsetUnits: 1,
			}),
			side: new MeshStandardMaterial({
				transparent: true,
				opacity: 0,
			}),
		},
		hover: {
			top: new MeshBasicMaterial({
				color: RoomWall.COLORS.HOVER,
				side: DoubleSide,
			}),
		},
		dragging: {
			top: new MeshStandardMaterial({
				color: RoomWall.COLORS.DRAGGING,
				side: DoubleSide,
			}),
		},
	};

	constructor(
		renderer: ProjectRenderer,
		private manager: RoomManager,
		start: RoomVertex,
		end: RoomVertex
	) {
		super(renderer);

		this.start = start;
		this.end = end;

		this.position = new Vector2()
			.addVectors(start.position, end.position)
			.multiplyScalar(0.5);

		this.length = this.start.position.distanceTo(this.end.position);

		this.geometry = new BoxGeometry();
		this.mesh = new Mesh(this.geometry, this.getMaterials());

		this.angle = Math.atan2(
			this.end.position.y - this.start.position.y,
			this.end.position.x - this.start.position.x
		);
		this.group.rotation.y = -this.angle;

		this.mesh.layers.set(RoomWall.LAYER);
		this.mesh.userData[UserDataProp.OBJECT_TYPE] = UserDataObjectType.WALL;
		this.mesh.userData[UserDataProp.OBJECT_ID] = this.id;

		this.group.add(this.mesh);
		this.renderer.room.add(this.group);
		this.initPopover();

		if (import.meta.env.DEV) this.renderer.scene.add(this.boxHelper);

		this.compute();
		this.addEventListeners();
	}

	override set isMouseOver(value: boolean) {
		if (this._isMouseOver === value) return;
		this._isMouseOver = value;
		this.mesh.material = this.getMaterials();
	}

	override set isDragging(value: boolean) {
		if (this._isDragging === value) return;
		this._isDragging = value;
		this.mesh.material = this.getMaterials();
	}

	private getMaterials() {
		// 0 – lewa
		// 1 – prawa
		// 2 – góra
		// 3 – dół
		// 4 – przód
		// 5 – tył

		let topMaterial: Material = this.materials.base.top;
		if (this._isMouseOver) topMaterial = this.materials.hover.top;
		if (this._isDragging) topMaterial = this.materials.dragging.top;

		const sideMaterial = this.materials.base.side,
			externalMaterial = this.materials.base.external,
			internal = this.materials.base.internal;

		return [
			sideMaterial,
			sideMaterial,
			topMaterial,
			this.materials.base.bottom,
			externalMaterial,
			internal,
		];
	}

	private initPopover() {
		this.popover = document.createElement('div');
		this.popover.className = 'renderer-popover';

		this.input = document.createElement('input');
		this.input.className = 'renderer-input';
		this.input.inputMode = 'numeric';

		this.input.min = RoomWall.MIN_LENGTH.toString();
		this.input.max = RoomWall.MAX_LENGTH.toString();
		this.input.step = '1';
		this.input.readOnly = false;

		this.popover.appendChild(this.input);

		this.css2dObject = new CSS2DObject(this.popover);
		this.css2dObject.position.set(
			this.position.x,
			ROOM_WALL.LABEL.POS_Y,
			this.position.y
		);
		this.css2dObject.layers.set(RoomWall.LABEL.LAYER);
		this.renderer.scene.add(this.css2dObject);

		this.css2dObject.visible = true;
	}

	private addEventListeners() {
		this.input.addEventListener(
			'input',
			debounce(({ target }) => {
				const sanitized = target.value
					.replace(',', '.')
					.replace(/[^0-9.]/g, '');
				const value = Number(sanitized);

				this.length = clamp(value, RoomWall.MIN_LENGTH, RoomWall.MAX_LENGTH);

				this.input.value = `${Math.round(this.length * 100) / 100} m`;
				this.resize(this.length);
			}, 1000)
		);
	}

	private createEdges() {
		if (this.edgeLines) RenderUtils.disposeObject(this.edgeLines);

		const bbox = new Box3().setFromBufferAttribute(
			this.mesh.geometry.attributes.position as BufferAttribute
		);

		const min = bbox.min;
		const max = bbox.max;

		const basePoints = [
			new Vector3(min.x, min.y, min.z),
			new Vector3(max.x, min.y, min.z),
			new Vector3(max.x, min.y, max.z),
			new Vector3(min.x, min.y, max.z),
		];

		const topPoints = [
			new Vector3(min.x, max.y, min.z),
			new Vector3(max.x, max.y, min.z),
			new Vector3(max.x, max.y, max.z),
			new Vector3(min.x, max.y, max.z),
		];

		const vertices = [];

		vertices.push(
			...basePoints[0].toArray(),
			...basePoints[1].toArray(),
			...basePoints[2].toArray(),
			...basePoints[3].toArray()
		);

		vertices.push(
			...topPoints[0].toArray(),
			...topPoints[1].toArray(),
			...topPoints[2].toArray(),
			...topPoints[3].toArray()
		);

		const geometry = new BufferGeometry();
		geometry.setAttribute('position', new Float32BufferAttribute(vertices, 3));

		const material = new LineBasicMaterial({
			color: RENDERER_CONFIG.COLORS.BLACK,
		});

		this.edgeLines = new LineSegments(geometry, material);
		this.edgeLines.layers.set(RoomWall.LINES_CONGIF.LAYER);
		this.group.add(this.edgeLines);
	}

	getWallAxis() {
		const size = new Vector3();
		this.box.getSize(size);
		return size.x > size.z ? 'x' : 'z';
	}

	resize(newLength: number) {
		const currentLength = this.start.position.distanceTo(this.end.position);
		const lengthDifference = newLength - currentLength;

		const direction = new Vector2()
			.subVectors(this.end.position, this.start.position)
			.normalize();

		const offset = direction.clone().multiplyScalar(lengthDifference / 2);

		const midPoint = new Vector2()
			.addVectors(this.start.position, this.end.position)
			.multiplyScalar(0.5);

		for (const vertex of this.manager.vertexList.toArray()) {
			const vecToVertex = new Vector2().subVectors(vertex.position, midPoint);

			const dotProduct = vecToVertex.dot(direction);

			if (dotProduct > 0) vertex.position = vertex.position.clone().add(offset);
			else vertex.position = vertex.position.clone().sub(offset);
		}

		this.manager.updateRoom();
	}

	handleResize(newLength: number) {
		const currentLength = this.start.position.distanceTo(this.end.position);
		if (currentLength === 0 || newLength === currentLength) return;

		const direction = new Vector2()
			.subVectors(this.end.position, this.start.position)
			.normalize();

		const parallelStart = this.start.prev;
		const parallelEnd = this.end.next;

		const lengthDifference = newLength - currentLength;

		const endOffset = direction.clone().multiplyScalar(lengthDifference / 2);

		this.end.position = this.end.position.clone().add(endOffset);
		if (parallelEnd)
			parallelEnd.position = parallelEnd.position.clone().add(endOffset);

		this.start.position = this.start.position.clone().sub(endOffset);
		if (parallelStart)
			parallelStart.position = parallelStart.position.clone().sub(endOffset);

		this.manager.updateRoom();
	}

	compute() {
		this.length = this.start.position.distanceTo(this.end.position);

		this.geometry.dispose();
		this.geometry = new BoxGeometry(
			this.length - ROOM_WALL.THICKNESS,
			this.manager.roomHeight,
			ROOM_WALL.THICKNESS
		);
		this.mesh.geometry = this.geometry;

		this.position = new Vector2()
			.addVectors(this.start.position, this.end.position)
			.multiplyScalar(0.5);

		this.group.position.set(
			this.position.x,
			this.manager.roomHeight / 2 + ROOM_WALL.POS_Y,
			this.position.y
		);

		const direction = new Vector2()
			.subVectors(this.end.position, this.start.position)
			.normalize();
		const perpendicular = new Vector2(-direction.y, direction.x);
		const labelPos = new Vector2().addVectors(
			this.position,
			perpendicular.multiplyScalar(RoomWall.LABEL.OFFSET_TO_WALL)
		);
		this.css2dObject.position.set(labelPos.x, 0.5, labelPos.y);

		this.input.value = `${Math.round(this.length * 100) / 100} m`;

		this.createEdges();
		this.updateBox();
	}

	dragHostedLandscapeModules(delta: Vector2) {
		const landscapeModules =
			this.renderer.modulesManager.getLandscapeModulesForWall(this);

		for (const landscapeModule of landscapeModules) {
			landscapeModule.position = landscapeModule.position
				.clone()
				.add(new Vector3(delta.x, 0, delta.y));
		}
	}

	dispose() {
		this.css2dObject.element.remove();
		RenderUtils.disposeObject(this.group);
		RenderUtils.disposeObject(this.css2dObject);
		RenderUtils.disposeObject(this.boxHelper);
	}
}
